import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { Button, Container, Form, Input, Label, FormGroup } from 'reactstrap';
import { connect } from 'react-redux';
import { verifyCodeActions } from '../../../../core/sesion-verify/actions';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import LoadingIndicator from '../../../components/loading-indicator';
import { api } from '../../../../core/api/api-service';
import { formatCountdownTime } from '../../../../core/helper';
import { ModalVerificationSuccess } from '../ModalVerificationSuccess';

const ConfirmRegister = ({ register, createVerify, verifySesionCode, resetVerify }) => {
    const [verificationCode, setVerificationCode] = useState('');
    const [resendPending, setResendPending] = useState(false);
    const [statusPending, setStatusPending] = useState(false);
    const [resendError, setResendError] = useState(null);
    const [secondsLeft, setSecondsLeft] = useState(null);
    const [disable, setDisabled] = useState(true);
    const [waitedTenSeconds, setWaitedTenSeconds] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const nodeRef = useRef(null);
    const searchParams = new URLSearchParams(location.search);
    const tokenIdFromUrl = searchParams.get('token_id');
    const idFromUrl = searchParams.get('id');
    const idFromState = register?.id;
    const tokenIdFromState = register?.data?.token_id;
    const verificationTarget = useMemo(() => {
        if (tokenIdFromUrl) return { token_id: tokenIdFromUrl };
        if (tokenIdFromState) return { token_id: tokenIdFromState };
        if (idFromUrl) return { id: idFromUrl };
        if (idFromState) return { id: idFromState };
        return {};
    }, [tokenIdFromUrl, idFromUrl, idFromState, tokenIdFromState]);
    const hasVerificationTarget = Boolean(verificationTarget.token_id || verificationTarget.id);
    const [animationClass] = useState({
        appearActive: 'animate__animated animate__slideInRight',
        enter: 'animate__animated',
        enterActive: 'animate__animated animate__slideInRight',
        exit: 'animate__animated',
        exitActive: 'animate__animated animate__slideOutLeft',
    });

    const applyTimerFromStatus = (status = {}) => {
        setSecondsLeft(Math.max(0, Number(status.seconds_remaining) || 0));
    };

    const loadVerificationStatus = async ({ cancelled } = {}) => {
        setStatusPending(true);
        setResendError(null);

        try {
            const res = await api.getVerificationStatus(verificationTarget);
            if (cancelled?.()) return;

            if (res?.success && res?.data) {
                if (res.data.already_verified) {
                    navigate('/welcome');
                    return;
                }
                applyTimerFromStatus(res.data);
            } else {
                setSecondsLeft(0);
            }
        } catch (error) {
            if (!cancelled?.()) setSecondsLeft(0);
        } finally {
            if (!cancelled?.()) setStatusPending(false);
        }
    };

    useEffect(() => {
        if (!hasVerificationTarget) return undefined;

        let cancelled = false;
        loadVerificationStatus({ cancelled: () => cancelled });

        return () => {
            cancelled = true;
        };
    }, [hasVerificationTarget, verificationTarget.token_id, verificationTarget.id]);

    useEffect(() => {
        if (secondsLeft === null || secondsLeft <= 0) return undefined;

        const intervalId = setInterval(() => {
            setSecondsLeft((prev) => Math.max(0, prev - 1));
        }, 1000);

        return () => clearInterval(intervalId);
    }, [secondsLeft !== null && secondsLeft > 0]);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setWaitedTenSeconds(true);
        }, 10000);

        return () => clearTimeout(timeoutId);
    }, []);

    useEffect(() => {
        verificationCode.length > 1 && hasVerificationTarget ?
            setDisabled(false) :
            setDisabled(true)
    }, [verificationCode, hasVerificationTarget])

    const goToLogin = useCallback(() => {
        navigate('/welcome');
    }, [navigate]);

    useEffect(() => {
        if (!verifySesionCode.isNew && verifySesionCode.success) {
            setShowSuccessModal(true);
        }
    }, [verifySesionCode])

    useEffect(() => {
        if(!hasVerificationTarget) {
            navigate('/welcome')
        }

        return () => {
            resetVerify()
        }
    }, [hasVerificationTarget, navigate, resetVerify])

    const handleInputChange = async ({ target }) => {
        const { value } = target;
        setVerificationCode(value);
    }

    const onSubmitRegister = async (e) => {
        e.preventDefault();
        createVerify({
            code: verificationCode,
            ...verificationTarget,
        })
    }

    const onResendCode = async () => {
        if ((secondsLeft !== null && secondsLeft > 0) || resendPending || !hasVerificationTarget) return;

        setResendPending(true);
        setResendError(null);

        try {
            const res = await api.resendVerification(verificationTarget);
            if (res?.success) {
                await loadVerificationStatus();
            } else {
                setResendError(res?.data || res?.message || 'Unable to resend code');
            }
        } catch (error) {
            setResendError(error?.message || 'Unable to resend code');
        } finally {
            setResendPending(false);
        }
    }

    const codeExpired = secondsLeft !== null && secondsLeft <= 0;
    const timerReady = secondsLeft !== null;
    const showCodeHelp = waitedTenSeconds && !verificationCode.trim();

    return (
        <Container fluid="xl" className='minvh-100 overflow-hidden position-relative px-0 px-md-3'>
            <TransitionGroup component={null}>
                <CSSTransition nodeRef={nodeRef} classNames={animationClass} timeout={{ enter: 1000, exit: 1000 }} key='confirm' appear>
                    <div ref={nodeRef} className='d-flex flex-column align-items-center minvh-100'>
                        <div className="forgot-password-page register-confirm-page bg-white p-4 mt-0 my-md-auto">
                            <div className='mb-3 pt-md-3 d-flex align-items-center'>
                                <h1 className='libre-caslon-text-regular h3-md h4 mb-0'>Register</h1>
                            </div>

                            <Form onSubmit={onSubmitRegister} >
                                <FormGroup className="mb-2">
                                    <Label for="code">We sent a verification code to your email</Label>
                                    <Input
                                        type="text"
                                        name="verificationCode"
                                        id="code"
                                        placeholder="Please enter your verification code"
                                        value={verificationCode}
                                        onChange={handleInputChange}
                                    />

                                    {showCodeHelp ? (
                                        <p className="m-0 mt-2 small text-muted">
                                            <b>Didn't receive the code? </b>
                                            The email can take a few minutes to generate and may have ended up in your spam folder. If you still cannot locate or need assistance, please contact us{' '}
                                            <Link to="/contact-us" className="text-primary font-weight-semibold">here</Link>.
                                        </p>
                                    ) : null}

                                    <p className={`m-0 mt-2 small ${codeExpired ? 'text-danger' : 'text-muted'}`}>
                                        {!timerReady
                                            ? 'Checking code expiration...'
                                            : codeExpired
                                                ? 'Your verification code has expired.'
                                                : `Code expires in ${formatCountdownTime(secondsLeft)}`}
                                    </p>

                                    <Button
                                        color="link"
                                        type="button"
                                        className='small text-underline p-0 m-0'
                                        onClick={onResendCode}
                                        disabled={!timerReady || !codeExpired || resendPending || !hasVerificationTarget}
                                    >
                                        {resendPending ? 'Resending...' : 'Resend code'}
                                    </Button>
                                </FormGroup>
                                
                                <Button color="green-light" size='sm' type="submit" disabled={disable || !timerReady || codeExpired} className='py-2 rounded-sm mb-2 btn-block'>Verify</Button>
                            </Form>

                            {!verifySesionCode.success && <p> {verifySesionCode.error} </p>}
                            {resendError ? <p className="text-danger">{resendError}</p> : null}

                        </div>
                    </div>
                </CSSTransition>
            </TransitionGroup>

            <ModalVerificationSuccess
                isOpen={showSuccessModal}
                onRedirect={goToLogin}
            />

            {
                (verifySesionCode.isPending || resendPending || statusPending) &&
                <LoadingIndicator />
            }
        </Container>
    )
}


const mapStateToProps = (state, props) => {
    const { register, verifySesionCode } = state;
    return {
        register,
        verifySesionCode
    };
};

const mapDispatchToProps = {
    createVerify: verifyCodeActions.createVerify,
    resetVerify: verifyCodeActions.resetVerify
}

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(ConfirmRegister);
